import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import BreakingNews from '../components/BreakingNews';
import Footer from '../components/Footer';
import { Search, Calendar, FileText, Download, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { getImageUrl, getPdfUrl } from '../utils/imageHelper';
import { formatDate } from '../utils/dateFormatter';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=1000&fit=crop';

const EPaperSearchPage = () => {
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    exactPhrase: '',
    date: '',
    dateFrom: '',
    dateTo: '',
    month: '',
    year: '',
  });
  
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      if (searchParams.keyword) params.append('keyword', searchParams.keyword);
      if (searchParams.exactPhrase) params.append('exact_phrase', searchParams.exactPhrase);
      if (searchParams.date) params.append('date', searchParams.date);
      if (searchParams.dateFrom) params.append('date_from', searchParams.dateFrom);
      if (searchParams.dateTo) params.append('date_to', searchParams.dateTo);
      if (searchParams.month) params.append('month', searchParams.month);
      if (searchParams.year) params.append('year', searchParams.year);
      
      const response = await axios.get(`${API_URL}/api/epapers/search/query?${params.toString()}`);
      setResults(response.data.results);
      setTotalResults(response.data.total);
    } catch (error) {
      console.error('Search failed:', error);
      if (error.response?.status === 400) {
        alert('Please enter at least one search parameter');
      } else {
        alert('Search failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchParams({
      keyword: '',
      exactPhrase: '',
      date: '',
      dateFrom: '',
      dateTo: '',
      month: '',
      year: '',
    });
    setResults([]);
    setSearched(false);
    setTotalResults(0);
  };

  const openEpaper = (pdfUrl) => {
    const fullUrl = getPdfUrl(pdfUrl, '');
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <BreakingNews />

      <div className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            ই-পেপার সার্চ
          </h1>
          <p className="text-xl text-gray-600">E-Paper Search & Archive</p>
          <p className="text-gray-500 mt-2">
            Search through our complete newspaper archive by keyword, date, or publication period
          </p>
        </div>

        {/* Search Box */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Main Search Input */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by any word (e.g., Agartala, Chief Minister, শিক্ষা, পুলিশ)"
                    value={searchParams.keyword}
                    onChange={(e) => setSearchParams({ ...searchParams, keyword: e.target.value })}
                    className="pl-10 h-12 text-base"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button 
                  onClick={handleSearch}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 h-12 px-8"
                >
                  {loading ? 'Searching...' : 'Search'}
                </Button>
                <Button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  variant="outline"
                  className="h-12"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </Button>
              </div>

              {/* Advanced Filters */}
              {showAdvanced && (
                <div className="border-t pt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Exact Phrase */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Exact Phrase
                      </label>
                      <Input
                        type="text"
                        placeholder="Search exact phrase"
                        value={searchParams.exactPhrase}
                        onChange={(e) => setSearchParams({ ...searchParams, exactPhrase: e.target.value })}
                      />
                    </div>

                    {/* Specific Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specific Date
                      </label>
                      <Input
                        type="date"
                        value={searchParams.date}
                        onChange={(e) => setSearchParams({ ...searchParams, date: e.target.value })}
                      />
                    </div>

                    {/* Date From */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date From
                      </label>
                      <Input
                        type="date"
                        value={searchParams.dateFrom}
                        onChange={(e) => setSearchParams({ ...searchParams, dateFrom: e.target.value })}
                      />
                    </div>

                    {/* Date To */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date To
                      </label>
                      <Input
                        type="date"
                        value={searchParams.dateTo}
                        onChange={(e) => setSearchParams({ ...searchParams, dateTo: e.target.value })}
                      />
                    </div>

                    {/* Month */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Month (YYYY-MM)
                      </label>
                      <Input
                        type="month"
                        value={searchParams.month}
                        onChange={(e) => setSearchParams({ ...searchParams, month: e.target.value })}
                      />
                    </div>

                    {/* Year */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Year
                      </label>
                      <Input
                        type="number"
                        placeholder="e.g., 2026"
                        value={searchParams.year}
                        onChange={(e) => setSearchParams({ ...searchParams, year: e.target.value })}
                        min="1974"
                        max={new Date().getFullYear()}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={clearSearch}
                      variant="outline"
                      size="sm"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Search Results */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Searching archives...</p>
          </div>
        ) : searched ? (
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {totalResults === 0 ? 'No Results Found' : `${totalResults} Result${totalResults > 1 ? 's' : ''} Found`}
              </h2>
              {totalResults > 0 && (
                <p className="text-gray-600 mt-1">
                  Showing {results.length} of {totalResults} e-papers
                </p>
              )}
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((epaper) => (
                  <div
                    key={epaper.id}
                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    {/* E-Paper Preview */}
                    <div className="relative h-96 bg-gray-100 flex items-center justify-center border-b border-gray-200">
                      {epaper.thumbnail_url ? (
                        <img
                          src={getImageUrl(epaper.thumbnail_url)}
                          alt={epaper.title}
                          className="w-full h-full object-contain"
                          onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                        />
                      ) : (
                        <div className="text-center">
                          <FileText className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-400 font-medium">PDF Document</p>
                        </div>
                      )}
                    </div>

                    {/* E-Paper Info */}
                    <div className="p-5">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {epaper.title || 'Tripura Darpan'}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(epaper.date)}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => openEpaper(epaper.pdf_url)}
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Read Now
                        </Button>
                        <Button
                          onClick={() => window.open(getPdfUrl(epaper.pdf_url, ''), '_blank')}
                          variant="outline"
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-lg">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No Results Found
                </h3>
                <p className="text-gray-500">
                  Try different search terms or adjust your filters
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-lg">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Start Your Search
            </h3>
            <p className="text-gray-500">
              Enter keywords, dates, or use advanced filters to search our newspaper archive
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default EPaperSearchPage;
